import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SwivuList } from "@/modern/components/swivu-list";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useState, useEffect, useMemo } from "react";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/modern/components/ui/tabs";
import { Combobox } from "@/modern/components/ui/combobox";
import { UserAvatar, BoardUser } from "@/modern/components/user-avatar";
import { INCENTIVE_PERMISSION_KEYS } from "@/other/constants/IncentiveKeys";
import { Button } from "@/modern/components/ui/button";
import { UserPlus } from "lucide-react";

interface ViewPermissionItem {
  id: string;
  label: string;
  value: number | null;
}

interface ManagePermissionItem {
  id: string;
  label: string;
  value: number[];
}

const VIEW_GRID_DATA: Omit<ViewPermissionItem, "value">[] = [
  { id: INCENTIVE_PERMISSION_KEYS.INCENTIVE_VIEW, label: 'Incentives' },
  { id: INCENTIVE_PERMISSION_KEYS.PENDING_POINTS_VIEW, label: 'Pending Points' },
  { id: INCENTIVE_PERMISSION_KEYS.APPROVED_POINTS_VIEW, label: 'Approved Points' },
  { id: INCENTIVE_PERMISSION_KEYS.DENIED_POINTS_VIEW, label: 'Denied Points' },
  { id: INCENTIVE_PERMISSION_KEYS.AWARDED_POINTS_VIEW, label: 'Awarded Points' },
];

const MANAGE_GRID_DATA: Omit<ManagePermissionItem, "value">[] = [
  { id: INCENTIVE_PERMISSION_KEYS.INCENTIVE_MANAGERS, label: 'Can manage Incentives' },
  { id: INCENTIVE_PERMISSION_KEYS.POINT_APPROVERS, label: 'Can approve or deny points' },
  { id: INCENTIVE_PERMISSION_KEYS.POINT_AWARDERS, label: 'Can award approved points' },
  { id: INCENTIVE_PERMISSION_KEYS.POINT_ARCHIVERS, label: 'Can archive points' },
  { id: INCENTIVE_PERMISSION_KEYS.POINT_DELETERS, label: 'Can delete points' },
];

const IncentivePermissionDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user, swivuAppGrants } = useSwivuBryntumContext();
  const [viewPermissions, setViewPermissions] = useState<ViewPermissionItem[]>([]);
  const [managePermissions, setManagePermissions] = useState<ManagePermissionItem[]>([]);
  const [boardUsers, setBoardUsers] = useState<BoardUser[]>([]);

  const canEdit = useMemo<boolean>(() => {
    if (!user) return false;
    return user.can(GRANTS.grant_one);
  }, [user]);

  const { subscribe: subscribePreferences } = useSwivuStore({
    storeId: STORE_KEYS.incentiveBoardPreferencesStore,
  });

  const { subscribe: subscribeBoardUsers } = useSwivuStore({
    storeId: STORE_KEYS.boardUserStore,
  });

  const loadPermissions = () => {
    if (!project) return;
    const incentiveBoardPreferencesStore = project.incentiveBoardPreferencesStore;
    if (!incentiveBoardPreferencesStore) return;

    const incentiveBoardPreferences = incentiveBoardPreferencesStore.first;
    if (!incentiveBoardPreferences) return;

    const viewData = VIEW_GRID_DATA.map(item => ({
      ...item,
      value: incentiveBoardPreferences[item.id] as number | null,
    }));

    const manageData = MANAGE_GRID_DATA.map(item => ({
      ...item,
      value: (incentiveBoardPreferences[item.id] || []) as number[],
    }));

    setViewPermissions(viewData);
    setManagePermissions(manageData);
  };

  const loadBoardUsers = () => {
    if (!project) return;
    const boardUsersStore = project.boardUsersStore;
    if (!boardUsersStore) return;
    const userRecords = (boardUsersStore.query(() => true, true) || []) as unknown as BoardUser[];
    setBoardUsers(userRecords);
  };

  useEffect(() => {
    loadPermissions();
    return subscribePreferences(() => {
      loadPermissions();
    });
  }, [subscribePreferences]);

  useEffect(() => {
    loadBoardUsers();
    return subscribeBoardUsers(() => {
      loadBoardUsers();
    });
  }, [subscribeBoardUsers]);

  const handleViewPermissionChange = (item: ViewPermissionItem, newValue: string | number | string[] | number[] | null) => {
    if (!project) return;
    const incentiveBoardPreferencesStore = project.incentiveBoardPreferencesStore;
    if (!incentiveBoardPreferencesStore) return;

    const incentiveBoardPreferences = incentiveBoardPreferencesStore.first;
    if (!incentiveBoardPreferences) return;

    const grantValue = newValue === null ? null : Number(newValue);
    incentiveBoardPreferences[item.id] = grantValue;
  };

  const handleManagePermissionChange = (item: ManagePermissionItem, newValue: string | number | string[] | number[] | null) => {
    if (!project) return;
    const incentiveBoardPreferencesStore = project.incentiveBoardPreferencesStore;
    if (!incentiveBoardPreferencesStore) return;

    const incentiveBoardPreferences = incentiveBoardPreferencesStore.first;
    if (!incentiveBoardPreferences) return;

    const userIds = Array.isArray(newValue) ? newValue.map(v => Number(v)) : [];
    incentiveBoardPreferences[item.id] = userIds;
  };

  const roleOptions = useMemo(() => {
    if (!swivuAppGrants || !Array.isArray(swivuAppGrants)) return [];
    return swivuAppGrants.map(grant => ({
      label: grant.label,
      value: grant.value.toString(),
    }));
  }, [swivuAppGrants]);

  const userOptions = useMemo(() => {
    return boardUsers.map(user => ({
      label: user.name,
      value: user.id.toString(),
    }));
  }, [boardUsers]);

  return (
    <div className="p-6 flex flex-col w-full h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <Tabs defaultValue="view" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="view" className="min-w-[12rem]">Can View Permissions</TabsTrigger>
          <TabsTrigger value="manage" className="min-w-[12rem]">Can Manage Permissions</TabsTrigger>
        </TabsList>

        {/* View Permissions Tab */}
        <TabsContent value="view" className="space-y-4">
          <div className="text-sm text-muted-foreground mb-4">
            Configure which roles can view different incentive sections.
          </div>
          <SwivuList.Root<ViewPermissionItem>
            items={viewPermissions}
            searchConfig={{
              fields: ["label"],
              placeholder: "Search view permissions...",
            }}
          >
            <SwivuList.Toolbar />

            <SwivuList.Header>
              <SwivuList.HeaderItem flex className="flex-1 min-w-[200px]">
                Can View
              </SwivuList.HeaderItem>
              <SwivuList.HeaderItem flex className="flex-1 min-w-[250px]">
                Permission Role
              </SwivuList.HeaderItem>
            </SwivuList.Header>

            <SwivuList.Content<ViewPermissionItem> emptyMessage="No view permissions found.">
              {(item: ViewPermissionItem) => (
                <SwivuList.Item value={item.id as string} className="hover:bg-muted/50">
                  {/* Label */}
                  <div className="flex-1 px-4 py-2">
                    <span className="text-sm font-medium text-foreground">
                      {item.label}
                    </span>
                  </div>

                  {/* Role Selection */}
                  <div className="flex-1 px-4 py-2">
                    <Combobox
                      options={roleOptions}
                      value={item.value?.toString() || ""}
                      onValueChange={(value) => handleViewPermissionChange(item, value as string | null)}
                      placeholder="Select role"
                      disabled={!canEdit}
                      showBadges={true}
                      size="sm"
                    />
                  </div>
                </SwivuList.Item>
              )}
            </SwivuList.Content>
          </SwivuList.Root>
        </TabsContent>

        {/* Manage Permissions Tab */}
        <TabsContent value="manage" className="space-y-4">
          <div className="text-sm text-muted-foreground mb-4">
            Configure which board users can perform management actions on incentives.
          </div>
          <SwivuList.Root<ManagePermissionItem>
            items={managePermissions}
            searchConfig={{
              fields: ["label"],
              placeholder: "Search manage permissions...",
            }}
          >
            <SwivuList.Toolbar />

            <SwivuList.Header>
              <SwivuList.HeaderItem flex>
                Can Manage
              </SwivuList.HeaderItem>
              <SwivuList.HeaderItem flex>
                Board Users
              </SwivuList.HeaderItem>
            </SwivuList.Header>

            <SwivuList.Content<ManagePermissionItem> emptyMessage="No manage permissions found.">
              {(item: ManagePermissionItem) => {
                const selectedUsers = boardUsers.filter(user =>
                  item.value.includes(user.id as number)
                );

                return (
                  <SwivuList.Item value={item.id as string} className="hover:bg-muted/50">
                    {/* Label */}
                    <div className="flex-1 px-4 py-2">
                      <span className="text-sm font-medium text-foreground">
                        {item.label}
                      </span>
                    </div>

                    {/* User Selection */}
                    <div className="flex items-center flex-1 gap-2">
                      {/* Show first 5 selected users with avatars */}
                      {selectedUsers.slice(0, 5).map(user => (
                        <UserAvatar
                          key={user.id}
                          user={user}
                          showTooltip={true}
                          showRemove={canEdit}
                          onRemove={() => {
                            const newValue = item.value.filter(id => id !== (user.id as number));
                            handleManagePermissionChange(item, newValue.map(v => v.toString()));
                          }}
                          avatarClassName="ring-0"
                        />
                      ))}

                      {/* Show count if more than 5 users */}
                      {selectedUsers.length > 5 && (
                        <div className="h-7 w-7 rounded-full bg-muted border-2 border-background flex items-center justify-center">
                          <span className="text-xs text-muted-foreground font-medium">
                            +{selectedUsers.length - 5}
                          </span>
                        </div>
                      )}
                      <Combobox
                        options={userOptions}
                        value={item.value.map(v => v.toString())}
                        onValueChange={(value) => handleManagePermissionChange(item, value as string[])}
                        placeholder="Select users"
                        searchPlaceholder="Search users..."
                        disabled={!canEdit}
                        multiSelect={true}
                        size="sm"
                        popoverContentClassName="w-max"
                        className="w-auto"
                        renderer={() => (
                          <>
                            {canEdit && (
                              <Button
                                variant="outline"
                                size="icon"
                                className="size-8"
                              >
                                <UserPlus className="h-4 w-4" />
                              </Button>
                            )}
                          </>
                        )}
                      />
                    </div>
                  </SwivuList.Item>
                );
              }}
            </SwivuList.Content>
          </SwivuList.Root>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default IncentivePermissionDetail;
